import uPlot from "uplot";
import { formatElapsedHHMMSS, formatGraphDate } from "../../utils/tigerTime";
import { edgeClampZoomPlugin } from "../../utils/edgeClampZoomPlugin";
import { readUiVar } from "../../theme/uiTheme";

// Axis/grid colours follow the UI theme. uPlot accepts functions here and
// re-evaluates them on every draw, so a theme change only needs a redraw
// (GraphWindow/GraphWindowXY call u.redraw() on the theme event). Under the
// default Acrylic Glass theme — and in the headless report renderer — these
// resolve to the original #333 / #e0e0e0.
const axisStroke = () => readUiVar("plotAxis", "#333");
const gridStroke = () => readUiVar("plotGrid", "#e0e0e0");

// f32::MAX — backend uses this as a sentinel for missing/invalid samples.
// Anything at or above this magnitude is treated as null so it doesn't blow
// up the y-axis auto-range and so empty/invalid plots don't break the chart.
const INVALID_TIGER_VALUE = 3.4028234663852886e38;

function sanitize(vals: number[]): (number | null)[] {
  return vals.map((v) => (Number.isFinite(v) && v < INVALID_TIGER_VALUE ? v : null));
}

export interface ChartSeries {
  label: string;
  color: string;
}

export type YScaleMode = "auto" | "normalize" | "manual";

export interface ChartBuildResult {
  opts: uPlot.Options;
  aligned: uPlot.AlignedData;
}

export interface TimeSeriesBuilderArgs {
  width: number;
  height: number;
  series: ChartSeries[];
  data: { ts: number[]; vals: number[] }[];
  visibility: boolean[];
  yScaleMode: YScaleMode;
  yManual?: { min: number; max: number };
  /**
   * X-axis rendering:
   *  - "absolute": treat ts as Unix seconds; show "HH:MM:SS\ndd/mm" (default).
   *  - "elapsed":  treat ts as elapsed seconds from the per-series start; show "HH:MM:SS"
   *               with no date. Used for multi-time graphs where every series is
   *               aligned to t=0.
   */
  xAxisMode?: "absolute" | "elapsed";
  /**
   * Absolute mode only: label ticks with the date alone ("dd/mm", no time).
   * Report graphs use this when the plotted range spans more than 3 days.
   */
  xDateOnly?: boolean;
  /**
   * Always connect the line across gaps (uPlot `spanGaps: true`) AND skip the
   * time-gap null insertion. Event graphs use this: their samples are the
   * event-model output over the event window and must render as one continuous
   * line with no breaks, even across missing/invalid points or large time
   * jumps.
   */
  spanGaps?: boolean;
}

export function buildTimeSeriesChart(args: TimeSeriesBuilderArgs): ChartBuildResult | null {
  const { width, height, series: seriesMeta, data, visibility, yScaleMode, yManual } = args;
  const xAxisMode: "absolute" | "elapsed" = args.xAxisMode ?? "absolute";

  // Per-series sanitized values (invalid → null) and the corresponding
  // (ts, vals) tables for the join. Empty series are tracked but excluded
  // from the join so they don't break it; we re-pad the joined output below
  // so the final aligned data still has one y-array per series.
  //
  // Gap breaking happens here, per series, BEFORE the union join (C++
  // tiger_plot::add_to_data_buffers inserts a null when two of the PLOT'S OWN
  // samples are >1.95 sample periods apart). The distinction matters after the
  // join: an explicit null is a real gap (uPlot breaks the line), while a slot
  // that only another series populated is `undefined` — an alignment artifact
  // uPlot's line path connects straight across. Doing the break on the shared
  // joined axis (as this used to) measured the spacing of OTHER turbines'
  // interleaved samples and nulled every hole, which shattered each series of
  // a multi-turbine graph into invisible isolated points (blank chart).
  //
  // The period is the MEDIAN actual spacing of the series' own data, not the
  // message sample_rate: that field can be wildly smaller than the true
  // cadence (a historical trend reported sample_rate=300 while its samples
  // were ~3300s apart), which makes the 1.95x threshold smaller than every
  // real gap. Absolute axis only; event graphs (spanGaps) never break.
  const sanitizedVals: (number | null)[][] = data.map((d) => sanitize(d.vals ?? []));
  const joinTables: uPlot.AlignedData[] = [];
  const joinSourceIdx: number[] = []; // joinTables[k] came from data[joinSourceIdx[k]]
  data.forEach(({ ts }, i) => {
    if (!(ts?.length > 0) || sanitizedVals[i].length === 0) return;
    let xs: number[] = ts;
    let ys: (number | null)[] = sanitizedVals[i];
    if (xAxisMode === "absolute" && !args.spanGaps && ts.length > 2) {
      const diffs: number[] = [];
      for (let j = 1; j < ts.length; j++) diffs.push(ts[j] - ts[j - 1]);
      const period = diffs.slice().sort((a, b) => a - b)[Math.floor(diffs.length / 2)];
      if (period > 0) {
        const threshold = 1.95 * period;
        const newXs: number[] = [];
        const newYs: (number | null)[] = [];
        for (let j = 0; j < ts.length; j++) {
          if (j > 0 && ts[j] - ts[j - 1] > threshold) {
            newXs.push(ts[j - 1] + period);
            newYs.push(null);
          }
          newXs.push(ts[j]);
          newYs.push(ys[j]);
        }
        xs = newXs;
        ys = newYs;
      }
    }
    joinTables.push([xs, ys]);
    joinSourceIdx.push(i);
  });
  if (joinTables.length === 0) return null;

  const joined = uPlot.join(joinTables);
  const xs = joined[0];

  // Re-pad: emit one y-array per series in seriesMeta order, filling absent
  // ones with nulls so series-config and data-array shapes stay aligned.
  // Without this, dropping any single empty/invalid plot from the join
  // shifted indices and uPlot refused to render the chart.
  const fullYSeries: (number | null)[][] = seriesMeta.map((_s, i) => {
    const k = joinSourceIdx.indexOf(i);
    if (k < 0) return new Array(xs.length).fill(null);
    return joined[k + 1] as (number | null)[];
  });
  const aligned: uPlot.AlignedData = [xs, ...fullYSeries];

  const scales: Record<string, uPlot.Scale> = {
    x: xAxisMode === "elapsed" ? { time: false } : { time: true },
  };
  const axes: uPlot.Axis[] = [
    {
      stroke: axisStroke,
      grid: { stroke: gridStroke, width: 1 },
      // Minimum px between x ticks. uPlot's default (50) is narrower than a
      // full "31/12/2012" label at the default axis font, so adjacent date
      // labels collided on long-range graphs; 90 keeps a visible gap.
      space: 90,
      values: (_u: uPlot, splits: number[]) =>
        xAxisMode === "elapsed"
          ? splits.map((v) => formatElapsedHHMMSS(v))
          : (() => {
              // Over 30 visible days the time of day is noise — show dates
              // only. splits track the display scale, so zooming under 30
              // days brings the time back. Report graphs force date-only
              // above 3 days via xDateOnly. Dates always carry the year.
              const span = splits.length > 1 ? splits[splits.length - 1] - splits[0] : 0;
              const dateOnly = args.xDateOnly || span > 30 * 24 * 3600;
              return splits.map((v) => {
                const d = new Date(v * 1000);
                const date = formatGraphDate(d);
                if (dateOnly) return date;
                const HH = String(d.getUTCHours()).padStart(2, "0");
                const MM = String(d.getUTCMinutes()).padStart(2, "0");
                const SS = String(d.getUTCSeconds()).padStart(2, "0");
                return `${HH}:${MM}:${SS}\n${date}`;
              });
            })(),
    },
  ];

  if (yScaleMode === "normalize") {
    seriesMeta.forEach((_s, i) => {
      const valid = sanitizedVals[i].filter((v): v is number => v != null);
      if (valid.length === 0) return;
      const min = Math.min(...valid);
      const max = Math.max(...valid);
      const pad = (max - min) * 0.05 || 1;
      const scaleKey = `y-${i}`;
      scales[scaleKey] = { auto: false, range: [min - pad, max + pad] };
      if (i === 0) {
        axes.push({
          scale: scaleKey,
          stroke: axisStroke,
          grid: { stroke: gridStroke, width: 1 },
          // Every series has its own normalized scale, so numeric labels
          // (they'd be the first series' values) would be misleading.
          values: (_u: uPlot, splits: number[]) => splits.map(() => ""),
        });
      }
    });
  } else if (yScaleMode === "manual") {
    const lo = yManual?.min ?? 0;
    const hi = yManual?.max ?? 1;
    scales["y"] = { auto: false, range: [lo, hi] };
    axes.push({ scale: "y", stroke: axisStroke, grid: { stroke: gridStroke, width: 1 } });
  } else {
    axes.push({ stroke: axisStroke, grid: { stroke: gridStroke, width: 1 } });
  }

  const series: uPlot.Series[] = [
    {},
    ...seriesMeta.map((s, i) => {
      const entry: uPlot.Series = {
        label: s.label,
        stroke: s.color,
        width: 2,
        show: visibility[i] !== false,
        // Event graphs draw one continuous line — connect across nulls/gaps.
        ...(args.spanGaps ? { spanGaps: true } : {}),
      };
      if (yScaleMode === "normalize") {
        entry.scale = `y-${i}`;
      } else if (yScaleMode === "manual") {
        entry.scale = "y";
      }
      return entry;
    }),
  ];

  const opts: uPlot.Options = {
    width,
    height,
    series,
    scales,
    axes,
    // Tiger timestamps are the site's local wall-clock encoded zone-neutrally,
    // and the tick labels decode them with UTC getters. Generate the splits on
    // the same basis so ticks land on the recorded wall-clock midnights: with
    // the default (browser-zone) tzDate a monthly tick sat at *browser* local
    // midnight — a different instant than the recorded midnight — so labels
    // showed month-ends (31/01, 28/02, …) instead of the 1st.
    tzDate: (ts: number) => uPlot.tzDate(new Date(ts * 1000), "Etc/UTC"),
    // Built-in drag is disabled; edgeClampZoomPlugin reimplements drag-to-zoom
    // so a selection dragged past a plot border clamps to the edge.
    cursor: { drag: { x: false, y: false } },
    legend: { show: false },
    plugins: [edgeClampZoomPlugin()],
  };

  return { opts, aligned };
}
